import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointpersonalDetails";
import { JointPersonalFormoneData } from "app/utils/types";
import { RootState } from "app/lib/store/store";
import { usePersonalDetails } from "app/hooks/applicant/usePersonalDetails";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import { APPLICANT } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import DynamicFormOne from "app/components/Individual/personalDetails/dynamicForms/DynamicFormOne";

const PartOne: React.FC = () => {
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps.savedIds
  );
  const { formOne, ...others } = useSelector(
    (state: RootState) => state.jointpersonalDetails.stepsData
  );
  const applicant = useSelector(
    (state: RootState) => state.loanTypesSteps.jointApplication
  );

  const { mutateAsync: submitPersonalDetails, isPending } =
    usePersonalDetails();

  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<JointPersonalFormoneData>({
    defaultValues: {
      yourDetails: {
        firstName: "",
        middleName: "",
        lastName: "",
        borrower: true,
      },
      coBorrowerDetails: {
        firstName: "",
        middleName: "",
        lastName: "",
        borrower: true,
      },
    },
  });

  const onSubmit = async (data: JointPersonalFormoneData) => {
    const { yourDetails, coBorrowerDetails } = data || {};

    if (!savedIds?.secondApplicantID) {
      toast.error("Applicant ID not found!");
      return;
    }

    const yourDetailsData = {
      ...yourDetails,
      borrower: yourDetails.borrower ? true : false,
    };
    const coBorrowerDetailsData = {
      ...coBorrowerDetails,
      borrower: coBorrowerDetails.borrower ? true : false,
    };

    const request1 = {
      url: `${APPLICANT}/${savedIds?.applicantID}/first`,
      data: yourDetailsData,
    };

    const request2 = {
      url: `${APPLICANT}/${savedIds?.secondApplicantID}/first`,
      data: coBorrowerDetailsData,
    };

    const storedData = {
      yourDetails: { ...yourDetailsData },
      coBorrowerDetails: { ...coBorrowerDetailsData },
    };

    try {
      const [response1, response2] = await Promise.all([
        submitPersonalDetails(request1),
        submitPersonalDetails(request2),
      ]);
      if (response1?.success && response2?.success) {
        dispatch(nextStep());
        dispatch(
          addStepsData({
            ...others,
            formOne: storedData,
          })
        );
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formOne && formOne?.yourDetails && formOne?.coBorrowerDetails) {
      reset({
        yourDetails: {
          firstName: formOne?.yourDetails?.firstName || "",
          lastName: formOne?.yourDetails?.lastName || "",
          middleName: formOne?.yourDetails?.middleName || "",
          borrower: formOne?.yourDetails?.borrower ? true : false,
        },
        coBorrowerDetails: {
          firstName: formOne?.coBorrowerDetails?.firstName || "",
          lastName: formOne?.coBorrowerDetails?.lastName || "",
          middleName: formOne?.coBorrowerDetails?.middleName || "",
          borrower: formOne?.coBorrowerDetails?.borrower ? true : false,
        },
      });
    } else {
      const { YourDetails, Coborrower } = applicant || {};
      if (YourDetails?.firstName) {
        setValue("yourDetails.firstName", YourDetails.firstName);
      }
      if (YourDetails?.lastName) {
        setValue("yourDetails.lastName", YourDetails.lastName);
      }
      if (Coborrower?.firstName) {
        setValue("coBorrowerDetails.firstName", Coborrower.firstName);
      }
      if (Coborrower?.lastName) {
        setValue("coBorrowerDetails.lastName", Coborrower.lastName);
      }
    }
  }, [formOne, reset]);

  return (
    <>
      <ProgressBar progress={0} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[40%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Personal Details" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-5 flex-col md:flex-col xl:flex-row gap-3 sm:gap-4 ">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <DynamicFormOne
                control={control}
                errors={errors}
                firstName="yourDetails.firstName"
                middleName="yourDetails.middleName"
                lastName="yourDetails.lastName"
                borrower="yourDetails.borrower"
              />
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <DynamicFormOne
                control={control}
                errors={errors}
                firstName="coBorrowerDetails.firstName"
                middleName="coBorrowerDetails.middleName"
                lastName="coBorrowerDetails.lastName"
                borrower="coBorrowerDetails.borrower"
              />
            </div>
          </div>

          <div className="flex justify-end mb-6">
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default PartOne;
